import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Key, 
  CheckCircle, 
  XCircle, 
  Phone, 
  Mail, 
  MapPin, 
  FileText, 
  X,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Building
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './CompaniesManagement.css';

const CompaniesManagement = () => {
  const { addNotification } = useNotifications();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password_hash: '',
    phone: '',
    cuit: '',
    address: '',
    notes: '',
    is_active: true
  });

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordCompany, setPasswordCompany] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompanies(data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      addNotification({ title: 'Error', message: 'No se pudieron cargar las empresas.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleOpenCreateModal = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      email: '',
      password_hash: '',
      phone: '',
      cuit: '',
      address: '',
      notes: '',
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      email: company.email || '',
      password_hash: '', // Leave empty unless changing
      phone: company.phone || '',
      cuit: company.cuit || '',
      address: company.address || '',
      notes: company.notes || '',
      is_active: company.is_active ?? true
    });
    setIsModalOpen(true);
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      addNotification({ title: 'Atención', message: 'El nombre y correo electrónico son obligatorios.', type: 'warning' });
      return;
    }

    if (!editingCompany && !formData.password_hash.trim()) {
      addNotification({ title: 'Atención', message: 'Debe ingresar una contraseña inicial para la empresa.', type: 'warning' });
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        cuit: formData.cuit.trim(),
        address: formData.address.trim(),
        notes: formData.notes.trim(),
        is_active: formData.is_active
      };

      if (formData.password_hash.trim()) {
        payload.password_hash = formData.password_hash.trim();
      }

      if (editingCompany) {
        const { error } = await supabase
          .from('companies')
          .update(payload)
          .eq('id', editingCompany.id);

        if (error) throw error;
        addNotification({ title: 'Éxito', message: 'Empresa actualizada correctamente.', type: 'success' });
      } else {
        const { error } = await supabase
          .from('companies')
          .insert([payload]);

        if (error) throw error;
        addNotification({ title: 'Éxito', message: 'Nueva empresa registrada correctamente.', type: 'success' });
      }

      setIsModalOpen(false);
      fetchCompanies();
    } catch (err) {
      console.error('Error saving company:', err);
      addNotification({ title: 'Error', message: `No se pudo guardar la empresa: ${err.message}`, type: 'error' });
    }
  };

  const handleToggleStatus = async (company) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ is_active: !company.is_active })
        .eq('id', company.id);

      if (error) throw error;
      addNotification({ title: 'Estado actualizado', message: `La empresa ${company.name} ahora está ${!company.is_active ? 'Activa' : 'Inactiva'}.`, type: 'info' });
      fetchCompanies();
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo cambiar el estado.', type: 'error' });
    }
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`¿Estás seguro de eliminar la empresa "${company.name}"? Esta acción no se puede deshacer.`)) {
      try {
        const { error } = await supabase
          .from('companies')
          .delete()
          .eq('id', company.id);

        if (error) throw error;
        addNotification({ title: 'Eliminado', message: `Empresa "${company.name}" eliminada.`, type: 'success' });
        fetchCompanies();
      } catch (err) {
        console.error(err);
        addNotification({ title: 'Error', message: 'No se pudo eliminar la empresa.', type: 'error' });
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      addNotification({ title: 'Atención', message: 'Ingrese la nueva contraseña.', type: 'warning' });
      return;
    }

    try {
      const { error } = await supabase
        .from('companies')
        .update({ password_hash: newPassword.trim() })
        .eq('id', passwordCompany.id);

      if (error) throw error;
      addNotification({ title: 'Contraseña Actualizada', message: `Contraseña de ${passwordCompany.name} cambiada.`, type: 'success' });
      setIsPasswordModalOpen(false);
      setNewPassword('');
      fetchCompanies();
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo actualizar la contraseña.', type: 'error' });
    }
  };

  const filteredCompanies = companies.filter(c => {
    const q = searchQuery.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.cuit || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="companies-management-container page-animate">
      <div className="companies-header">
        <div className="header-title-box">
          <div className="header-icon-box"><Building2 size={24} /></div>
          <div>
            <h1>EMPRESAS REGISTRADAS</h1>
            <p>Gestiona las empresas autorizadas para solicitar turnos para sus pacientes</p>
          </div>
        </div>

        <button className="btn-primary" onClick={handleOpenCreateModal}>
          <Plus size={18} /> REGISTRAR NUEVA EMPRESA
        </button>
      </div>

      <div className="companies-filter-bar glass-panel">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            placeholder="Buscar por Nombre, Email, CUIT o Teléfono..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && <X size={16} className="clear-icon" onClick={() => setSearchQuery('')} />}
        </div>
        <div className="count-badge">
          <span>{filteredCompanies.length} Empresas</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state glass-panel">
          <RefreshCw size={28} className="animate-spin" />
          <p>Cargando empresas registradas...</p>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="empty-state glass-panel">
          <Building size={48} style={{ opacity: 0.3 }} />
          <h3>No se encontraron empresas</h3>
          <p>No hay empresas registradas o no coinciden con la búsqueda.</p>
        </div>
      ) : (
        <div className="companies-grid">
          {filteredCompanies.map(company => (
            <div key={company.id} className={`company-card glass-panel ${!company.is_active ? 'inactive' : ''}`}>
              <div className="company-card-header">
                <div className="company-info-main">
                  <div className="company-avatar"><Building2 size={20} /></div>
                  <div>
                    <h3 className="company-name">{company.name}</h3>
                    <span className={`status-pill ${company.is_active ? 'active' : 'inactive'}`}>
                      {company.is_active ? 'ACTIVA' : 'INACTIVA'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="company-card-body">
                <div className="detail-row">
                  <Mail size={15} /> <span>{company.email}</span>
                </div>
                {company.cuit && (
                  <div className="detail-row">
                    <ShieldCheck size={15} /> <span>CUIT: {company.cuit}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="detail-row">
                    <Phone size={15} /> <span>{company.phone}</span>
                  </div>
                )}
                {company.address && (
                  <div className="detail-row">
                    <MapPin size={15} /> <span>{company.address}</span>
                  </div>
                )}
                {company.notes && (
                  <div className="detail-row notes">
                    <FileText size={15} /> <span>{company.notes}</span>
                  </div>
                )}
              </div>

              <div className="company-card-actions">
                <button 
                  className="btn-action edit" 
                  title="Editar Empresa"
                  onClick={() => handleOpenEditModal(company)}
                >
                  <Edit3 size={15} /> Editar
                </button>
                <button 
                  className="btn-action key" 
                  title="Cambiar Contraseña"
                  onClick={() => { setPasswordCompany(company); setIsPasswordModalOpen(true); }}
                >
                  <Key size={15} /> Clave
                </button>
                <button 
                  className={`btn-action toggle ${company.is_active ? 'deactivate' : 'activate'}`} 
                  title={company.is_active ? 'Desactivar' : 'Activar'}
                  onClick={() => handleToggleStatus(company)}
                >
                  {company.is_active ? <XCircle size={15} /> : <CheckCircle size={15} />}
                  {company.is_active ? 'Desactivar' : 'Activar'}
                </button>
                <button 
                  className="btn-action delete" 
                  title="Eliminar"
                  onClick={() => handleDeleteCompany(company)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear / Editar */}
      {isModalOpen && (
        <div className="appointment-modal-overlay">
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '550px' }}>
            <div className="appointment-modal-header premium-header">
              <div className="header-title">
                <Building2 size={20} />
                <span>{editingCompany ? 'EDITAR EMPRESA' : 'REGISTRAR NUEVA EMPRESA'}</span>
              </div>
              <button className="btn-close-modal" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
            </div>

            <form onSubmit={handleSaveCompany} className="appointment-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-group">
                <label>NOMBRE DE LA EMPRESA *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="Ej: Salud Integral S.A." 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>CORREO ELECTRÓNICO (LOGIN) *</label>
                  <input 
                    type="email" 
                    required 
                    placeholder="contacto@empresa.com" 
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{editingCompany ? 'NUEVA CONTRASEÑA (OPCIONAL)' : 'CONTRASEÑA INICIAL *'}</label>
                  <input 
                    type="password" 
                    required={!editingCompany} 
                    placeholder={editingCompany ? 'Dejar en blanco para mantener' : 'Contraseña de acceso'} 
                    value={formData.password_hash}
                    onChange={(e) => setFormData({ ...formData, password_hash: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>CUIT / TAX ID</label>
                  <input 
                    type="text" 
                    placeholder="30-12345678-9" 
                    value={formData.cuit}
                    onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>TELÉFONO DE CONTACTO</label>
                  <input 
                    type="text" 
                    placeholder="1122334455" 
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>DIRECCIÓN</label>
                <input 
                  type="text" 
                  placeholder="Av. Corrientes 1234, CABA" 
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>NOTAS INTERNAS / OBSERVACIONES</label>
                <textarea 
                  rows={3}
                  placeholder="Notas adicionales de facturación o contacto..." 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                <input 
                  type="checkbox" 
                  id="is_active_check"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="is_active_check" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>
                  Empresa Habilitada / Activa
                </label>
              </div>

              <div className="appointment-modal-footer" style={{ marginTop: '1rem', padding: '1rem 0 0 0' }}>
                <button type="button" className="btn-cancel-modal" onClick={() => setIsModalOpen(false)}>CANCELAR</button>
                <button type="submit" className="btn-save-appointment">
                  {editingCompany ? 'GUARDAR CAMBIOS' : 'CREAR EMPRESA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Cambiar Contraseña */}
      {isPasswordModalOpen && passwordCompany && (
        <div className="appointment-modal-overlay">
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '450px' }}>
            <div className="appointment-modal-header premium-header">
              <div className="header-title">
                <Key size={20} />
                <span>CAMBIAR CONTRASEÑA: {passwordCompany.name}</span>
              </div>
              <button className="btn-close-modal" onClick={() => setIsPasswordModalOpen(false)}><X size={20} /></button>
            </div>

            <form onSubmit={handleChangePassword} className="appointment-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-group">
                <label>NUEVA CONTRASEÑA *</label>
                <input 
                  type="password" 
                  required 
                  placeholder="Ingrese nueva clave de acceso" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div className="appointment-modal-footer" style={{ marginTop: '1rem', padding: '1rem 0 0 0' }}>
                <button type="button" className="btn-cancel-modal" onClick={() => setIsPasswordModalOpen(false)}>CANCELAR</button>
                <button type="submit" className="btn-save-appointment">
                  ACTUALIZAR CONTRASEÑA
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesManagement;
